import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCurrency, setCurrencyOverride, PLAN_AMOUNTS, formatMoney, perDayLabel } from "../lib/currency";
import { toast } from "sonner";
import { Check, AlertTriangle, Shield, Zap, Trophy, Users, Sparkles } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";

const PLANS = [
    { key: "monthly",   name: "Monthly",   per: "month",    save: null,        cycle: "Auto-renews monthly. Cancel anytime." },
    { key: "annual",    name: "Annual",    per: "year",     save: "Save 33%",  cycle: "Auto-renews annually. Cancel anytime." },
    { key: "quarterly", name: "Quarterly", per: "3 months", save: "Save 19%",  cycle: "Auto-renews every 3 months. Cancel anytime." },
];

// Goal-adaptive framing on the annual card — reinforces "this is a long
// commitment worth making" using copy that ties back to the ad's promise.
// Matches the goal-detector used on Landing.jsx.
const ANNUAL_ANCHOR = {
    build: "Muscle takes 12–24 months of consistency. Lock in your bulk year.",
    lose:  "Most fat-loss journeys take longer than 3 months. Commit to your leaner year.",
    recomp: "Recomp is a marathon — visible results in 12 weeks, real ones in 12 months.",
    default: "Lock in your best year of training.",
};
const TOP_BANNER_COPY = {
    build:  "Annual saves 33% — picked by lifters serious about size",
    lose:   "Annual saves 33% — picked by lifters serious about staying lean",
    recomp: "Annual saves 33% — picked by lifters serious about both",
    default: "Annual saves 33% — picked by most serious lifters",
};

function detectGoalFromParams(params) {
    try {
        const explicit = (params.get("goal") || "").toLowerCase().trim();
        if (["build", "lose", "recomp"].includes(explicit)) return explicit;
        const hay = ((params.get("utm_content") || "") + " " + (params.get("utm_campaign") || "")).toLowerCase();
        if (/\b(recomp|body\s*recomp|both)\b/.test(hay)) return "recomp";
        if (/(fat|lose|cut|shred|lean|deficit)/.test(hay)) return "lose";
        if (/(muscle|bulk|build|mass|hyper|strength|size|growth)/.test(hay)) return "build";
        return null;
    } catch { return null; }
}

const FEATURES = [
    "Every training program — Upper/Lower 4-day + Full Body 3-day",
    "60+ stable exercises with looped form demos + anatomical muscle maps",
    "Workout logger with RIR, rest timer, warm-up protocol, unilateral L/R tracking",
    "12-week strength charts, PR auto-detection, session history, body-measurement logs",
    "20-rank EXP progression system with shareable rank cards",
    "TDEE + macro calculator calibrated to your stats and goal",
    "Squad leaderboards + invite codes to train with friends",
    "Renewal reminder emails · dark & light modes · fully mobile",
];

const REASON_COPY = {
    trial_expired: {
        title: "Your 7 days are up. Don't lose your progress.",
        body: "Every set you logged, every PR you hit, your current rank — all still yours. Pick a plan and you're back in the same second. Trial is one-time only.",
    },
    subscription_expired: {
        title: "Your access lapsed",
        body: "Renew below to get back to the same rank, same PRs, same session history — nothing was deleted.",
    },
    canceled: {
        title: "You canceled — but your data is safe",
        body: "Every workout, every PR, every rank tier you earned is still here. Pick a plan to jump back in.",
    },
    no_subscription: {
        title: "One tap to unlock everything",
        body: "Every training program, the full exercise library, form guides, progress tracking, ranks — pick a plan below.",
    },
    past_due: {
        title: "Payment failed — quick fix",
        body: "Update your payment method to keep your streak, your rank, and your session history active.",
    },
    premium_required: { title: "Subscription required", body: "Pick a plan to continue." },
};

// Anchoring row: puts the price in context BEFORE the reader sees it. Classic
// "compared to" framing — trainer/coach/gym prices are checked and verifiable
// so this stays honest. Numbers are conservative on the coach/trainer side.
const ANCHOR_ITEMS = [
    { label: "Personal trainer", price: "$200+", per: "/ session" },
    { label: "Coaching app", price: "$30", per: "/ month" },
    { label: "Commercial gym", price: "$60", per: "/ month" },
];

const KEEP_IF_CANCEL = [
    { icon: Trophy, title: "Your rank & EXP", body: "20 tiers of progress you earned. Frozen exactly where you left it — pick up mid-tier the day you rejoin." },
    { icon: Zap, title: "Every PR & every set", body: "Full session history and every per-exercise personal record stays archived, forever." },
    { icon: Users, title: "Your squad & invites", body: "Squad membership, invite codes, and weekly leaderboard rank all persist for when you come back." },
];

const PRICING_FAQ = [
    {
        q: "Do I need a credit card for the 7-day trial?",
        a: "No. You register with email + password (or Google) and start training immediately. Nothing charges. Nothing auto-bills. You only enter payment info if you decide to subscribe after the 7 days.",
    },
    {
        q: "How does cancel work?",
        a: "Go to Account → Manage subscription → Cancel. Two taps. Stripe handles it — you keep access through the end of the period you already paid for, then it stops. No emails, no forms, no phone calls. If you cancel a monthly plan on day 5, you still have access until day 30.",
    },
    {
        q: "What if I skip a month or want to pause?",
        a: "Cancel and rejoin whenever. Your rank, PRs, session history, and squad membership all stay archived. Reactivating is a single tap on the pricing page — you land back exactly where you left off, no re-onboarding.",
    },
    {
        q: "Why is annual so much cheaper per day?",
        a: "Because muscle is a 12–24 month project, not a 30-day sprint. We price annual to reward the lifters who commit to the timeframe growth actually happens in. If you're not sure, start monthly — you can upgrade to annual anytime from Account.",
    },
    {
        q: "Do you charge in USD or CAD?",
        a: "Both. Stripe auto-detects your country at checkout — Canadian cards pay in CAD, US cards pay in USD, everywhere else defaults to USD. No exchange surprises, no hidden fees.",
    },
    {
        q: "What if the program doesn't work for me?",
        a: "It will — mechanical tension + 4–8 reps + double progression works on day one, on year ten, natural or enhanced. But if you decide it's not for you inside the 7-day trial, you close the tab and never get charged. If you subscribe and then decide within your first paid period that you'd rather leave, cancel from Account and you keep access until the paid period ends — no auto-renewal, no follow-up charges.",
    },
];

export default function Pricing() {
    const { user, checkAuth } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [loading, setLoading] = useState(null);
    const [portalLoading, setPortalLoading] = useState(false);
    const polling = useRef(false);
    const currency = useCurrency();

    const paywall = params.get("paywall") === "1";
    const reason = params.get("reason") || user?.premium_reason;
    const reasonCopy = paywall && reason ? REASON_COPY[reason] : null;

    const pollStatus = useCallback(async (sessionId, attempt = 0) => {
        if (attempt >= 10) {
            // Last-ditch effort before showing the timeout error: sync directly
            // against Stripe in case the webhook hasn't landed yet.
            try {
                const { data } = await api.post("/payments/sync");
                if (data.premium) {
                    await checkAuth();
                    navigate("/welcome");
                    return;
                }
            } catch (_) { /* fall through */ }
            toast.error("Payment confirmation timed out. Refresh in a minute if you were charged.");
            return;
        }
        try {
            const { data } = await api.get(`/payments/status/${sessionId}`);
            if (data.payment_status === "paid") {
                await checkAuth();
                navigate("/welcome");
                return;
            }
            if (data.status === "expired") { toast.error("Checkout session expired."); return; }
            setTimeout(() => pollStatus(sessionId, attempt + 1), 2500);
        } catch (e) {
            setTimeout(() => pollStatus(sessionId, attempt + 1), 3000);
        }
    }, [checkAuth, navigate]);

    useEffect(() => {
        const sid = params.get("session_id");
        if (sid && !polling.current) {
            polling.current = true;
            toast.info("Confirming payment…");
            pollStatus(sid);
        }
    }, [params, pollStatus]);

    const checkout = async (plan) => {
        if (!user) { navigate("/register"); return; }
        setLoading(plan);
        try {
            const { data } = await api.post("/payments/checkout", { plan, origin_url: window.location.origin });
            window.location.href = data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start checkout");
            setLoading(null);
        }
    };

    const openPortal = async () => {
        setPortalLoading(true);
        try {
            const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
            window.location.href = data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not open billing portal");
            setPortalLoading(false);
        }
    };

    const [syncing, setSyncing] = useState(false);
    const syncPayment = async () => {
        setSyncing(true);
        try {
            const { data } = await api.post("/payments/sync");
            if (data.premium) {
                await checkAuth();
                navigate("/welcome");
            } else {
                toast.error("We couldn't find an active payment on your account. If you were charged, contact support with your Stripe receipt.");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Sync failed. Please try again or contact support.");
        } finally {
            setSyncing(false);
        }
    };

    const activePlan = user?.subscription_status === "active" ? user?.subscription_plan : null;
    const hasActiveSub = !!activePlan && !!user?.stripe_subscription_id;
    const activePlanLabel = activePlan ? activePlan.charAt(0).toUpperCase() + activePlan.slice(1) : null;

    // Goal-adaptive pricing anchor — if the visitor arrived from a specific
    // ad campaign (or is coming from onboarding with a declared goal), swap
    // the annual-plan framing to reinforce their commitment.
    const goalMatch = detectGoalFromParams(params);
    const annualAnchor = ANNUAL_ANCHOR[goalMatch] || ANNUAL_ANCHOR.default;
    const topBanner = TOP_BANNER_COPY[goalMatch] || TOP_BANNER_COPY.default;
    const isWinback = params.get("winback") === "1";

    return (
        <div className="space-y-10" data-testid="pricing-page">
            {isWinback && (
                <div className="border border-accent-red bg-accent-red/10 p-6" data-testid="winback-banner">
                    <div className="font-display text-2xl font-black uppercase">Come back — pick up where you left off.</div>
                    <p className="mt-2 text-sm text-foreground/90">Your trial ended, but every set you logged is still on your dashboard. Pick a plan and you&apos;re back in — same rank, same PRs, same streak.</p>
                </div>
            )}
            {reasonCopy && (
                <div className="border border-accent-red bg-accent-red/10 p-6" data-testid="paywall-banner">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="mt-0.5 h-6 w-6 flex-none text-accent-red" />
                        <div className="flex-1">
                            <div className="font-display text-2xl font-black uppercase">{reasonCopy.title}</div>
                            <p className="mt-1 text-sm text-foreground/90">{reasonCopy.body}</p>
                            {user && (
                                <div className="mt-4 border-t border-accent-red/40 pt-3">
                                    <p className="text-xs text-foreground/80">Already paid but still locked out? Click below to re-sync with Stripe.</p>
                                    <button
                                        onClick={syncPayment}
                                        disabled={syncing}
                                        data-testid="sync-payment-btn"
                                        className="mt-2 rounded-sm border border-accent-red px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-accent-red hover:bg-accent-red hover:text-white disabled:opacity-60"
                                    >
                                        {syncing ? "Checking Stripe…" : "I paid — restore my access"}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="text-center">
                <div className="label-eyebrow text-accent-red">Pricing</div>
                <h1 className="font-display text-4xl font-black uppercase sm:text-6xl">Less than a coffee.<br />More than most coaches.</h1>
                <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground">7-day free trial for first-time accounts (one-time only). No card required to start. Every plan auto-renews so you stay locked in — cancel anytime from Account → Manage subscription in 2 taps.</p>
                <p className="mx-auto mt-2 max-w-xl text-[11px] uppercase tracking-wider text-muted-foreground" data-testid="pricing-currency-note">
                    Prices shown in {currency}. {currency === "USD"
                        ? "US checkout will charge in USD."
                        : "Stripe will convert automatically at checkout based on your country."}
                </p>
                {/* Manual currency override — auto-detection is ~95% right
                    but a Canadian on VPN or a US traveler needs a way to
                    correct the sticker. This tiny toggle costs nothing
                    and recovers the 5% edge cases. */}
                <div className="mx-auto mt-3 inline-flex items-center gap-1 border border-white/10 bg-white/[0.02] p-1" data-testid="currency-toggle">
                    <span className="pl-2 pr-1 text-[10px] uppercase tracking-widest text-white/40">Show in</span>
                    {["USD", "CAD"].map(c => (
                        <button
                            key={c}
                            type="button"
                            onClick={() => setCurrencyOverride(c)}
                            data-testid={`currency-toggle-${c.toLowerCase()}`}
                            aria-pressed={currency === c}
                            className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors ${currency === c ? "bg-accent-red text-white" : "text-white/60 hover:text-white"}`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
                <p className="mx-auto mt-5 inline-flex items-center gap-2 border border-accent-red/40 bg-accent-red/10 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-accent-red" data-testid="annual-save-banner">
                    <span className="h-1.5 w-1.5 bg-accent-red" />
                    {topBanner}
                </p>
            </div>

            {/* Anchoring row — the classic "compared to" pattern. Puts the
                real cost of getting programmed by a human into the reader's
                head BEFORE they see our numbers. Every price shown here is
                a conservative real-world benchmark. */}
            <div className="tactical-card border-white/10 p-6" data-testid="anchor-row">
                <div className="label-eyebrow text-white/60">What people usually pay to make progress</div>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {ANCHOR_ITEMS.map((a, i) => (
                        <div key={i} className="border border-white/10 bg-white/[0.02] p-4" data-testid={`anchor-item-${i}`}>
                            <div className="text-[10px] uppercase tracking-widest text-white/50">{a.label}</div>
                            <div className="mt-1 flex items-baseline gap-1">
                                <span className="font-display text-2xl font-black tabular-nums text-white">{a.price}</span>
                                <span className="text-[10px] uppercase tracking-widest text-white/50">{a.per}</span>
                            </div>
                        </div>
                    ))}
                    <div className="border-2 border-accent-red bg-accent-red/10 p-4" data-testid="anchor-hg">
                        <div className="text-[10px] uppercase tracking-widest text-accent-red">Hypergains</div>
                        <div className="mt-1 flex items-baseline gap-1">
                            <span className="font-display text-2xl font-black tabular-nums text-accent-red">{perDayLabel("annual", currency)}</span>
                        </div>
                        <div className="mt-1 text-[10px] uppercase tracking-widest text-white/60">on annual plan</div>
                    </div>
                </div>
            </div>

            {hasActiveSub && (
                <div className="tactical-card p-6" data-testid="active-sub-banner">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <div className="label-eyebrow text-accent-red">You&apos;re subscribed</div>
                            <p className="mt-1 text-sm">{activePlanLabel} plan — auto-renewing. {user.cancel_at_period_end ? "Cancel scheduled at period end." : "Cancel anytime."}</p>
                        </div>
                        <button onClick={openPortal} disabled={portalLoading} data-testid="manage-sub-btn" className="rounded-sm border border-border px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-secondary disabled:opacity-60">
                            {portalLoading ? "Opening…" : "Manage subscription"}
                        </button>
                    </div>
                </div>
            )}

            <div className="grid gap-4 md:grid-cols-3 md:items-stretch">
                {PLANS.map(p => {
                    const amount = PLAN_AMOUNTS[p.key][currency];
                    const isCurrent = hasActiveSub && activePlan === p.key;
                    const isAnnual = p.key === "annual";
                    // "Effective monthly" so lifters can compare quarterly & annual
                    // apples-to-apples against the monthly card.
                    const effectiveMonthly = p.key === "monthly" ? amount : (p.key === "quarterly" ? amount / 3 : amount / 12);
                    const monthlyAmount = PLAN_AMOUNTS.monthly[currency];
                    const vsMonthlySavings = Math.max(0, Math.round(monthlyAmount - effectiveMonthly));
                    return (
                        <div
                            key={p.key}
                            className={`relative tactical-card p-8 transition-all ${
                                isAnnual
                                    ? "border-2 border-accent-red shadow-[0_0_60px_-12px_rgba(239,68,68,0.45)] md:-translate-y-3 md:scale-[1.02] md:bg-gradient-to-b md:from-accent-red/10 md:to-transparent"
                                    : "border border-border opacity-95"
                            }`}
                            data-testid={`plan-${p.key}`}
                            data-best-value={isAnnual ? "1" : "0"}
                        >
                            {isAnnual && (
                                <div
                                    className="absolute -top-3 left-1/2 -translate-x-1/2 select-none whitespace-nowrap rounded-sm border border-accent-red bg-accent-red px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-white shadow-[0_4px_18px_-4px_rgba(239,68,68,0.7)]"
                                    data-testid="best-value-badge"
                                >
                                    Most popular · best value
                                </div>
                            )}
                            {p.save && <div className="label-eyebrow mb-2 text-accent-red">{p.save}</div>}
                            <h3 className="font-display text-2xl font-black uppercase">{p.name}</h3>
                            <div className="mt-4 flex items-baseline gap-1">
                                <span className="font-display text-5xl font-black" data-testid={`plan-${p.key}-price`}>{formatMoney(amount, currency)}</span>
                                <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground" data-testid={`plan-${p.key}-cur`}>{currency}</span>
                                <span className="ml-1 text-sm text-muted-foreground">/ {p.per}</span>
                            </div>

                            {/* Per-day math — biggest single conversion lever on
                                pricing pages ("Less than a coffee") — kept honest
                                by computing from PLAN_AMOUNTS. */}
                            <div className={`mt-3 flex items-center justify-between border-t pt-3 ${isAnnual ? "border-accent-red/40" : "border-border"}`}>
                                <div className="flex flex-col">
                                    <span className="text-[10px] uppercase tracking-widest text-white/50">Per day</span>
                                    <span className={`font-display text-2xl font-black tabular-nums ${isAnnual ? "text-accent-red" : "text-white"}`} data-testid={`plan-${p.key}-perday`}>
                                        {perDayLabel(p.key, currency)}
                                    </span>
                                </div>
                                {!p.key.includes("monthly") && vsMonthlySavings > 0 && (
                                    <div className="flex flex-col text-right">
                                        <span className="text-[10px] uppercase tracking-widest text-white/50">Vs monthly</span>
                                        <span className="text-sm font-bold tabular-nums text-emerald-400" data-testid={`plan-${p.key}-vsmonthly`}>
                                            Save {formatMoney(vsMonthlySavings * (p.key === "quarterly" ? 3 : 12), currency)} / {p.per === "3 months" ? "qtr" : "yr"}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <p className="mt-3 text-xs text-muted-foreground">{p.cycle}</p>
                            {isAnnual && (
                                <p className="mt-2 text-[11px] leading-relaxed text-white/80" data-testid="annual-goal-anchor">
                                    {annualAnchor}
                                </p>
                            )}
                            <button
                                onClick={() => checkout(p.key)}
                                disabled={loading === p.key || isCurrent}
                                data-testid={`checkout-${p.key}`}
                                className={`mt-6 w-full rounded-sm px-4 py-3 text-xs font-bold uppercase tracking-wider transition-all disabled:opacity-60 ${
                                    isAnnual
                                        ? "bg-accent-red text-white shadow-[0_4px_24px_-6px_rgba(239,68,68,0.6)] hover:bg-[color:var(--accent-hover)] hover:shadow-[0_4px_28px_-2px_rgba(239,68,68,0.85)]"
                                        : "border border-border bg-card text-foreground hover:border-accent-red/60 hover:bg-secondary"
                                }`}
                            >
                                {loading === p.key
                                    ? "Redirecting…"
                                    : isCurrent
                                        ? "Current plan"
                                        : isAnnual
                                            ? "Start training — save 33%"
                                            : p.key === "quarterly"
                                                ? "Start training — save 19%"
                                                : "Start training"}
                            </button>
                            <p className="mt-3 text-center text-[10px] uppercase tracking-widest text-white/40">
                                {p.key === "monthly" ? "7 days free · no card until day 8" : "Auto-renews · cancel in 2 taps"}
                            </p>
                        </div>
                    );
                })}
            </div>

            {/* Cancel-anytime chip — honest, automatic-Stripe-behavior only.
                No refund language (we don't automate refunds). This is the
                trust-cushion that de-risks the checkout click. */}
            <div className="flex flex-wrap items-center justify-center gap-3 text-center" data-testid="cancel-chip">
                <div className="inline-flex items-center gap-2 border border-white/15 bg-white/[0.03] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white/80">
                    <Shield className="h-3.5 w-3.5 text-accent-red" />
                    Cancel anytime · 2 taps · you keep access through the paid period
                </div>
            </div>

            {/* What you keep if you cancel — loss-aversion framing. Every
                claim here maps to real backend behavior (rank, PRs, squad,
                history all persist in MongoDB regardless of subscription). */}
            <div className="tactical-card p-8" data-testid="keep-if-cancel">
                <div className="label-eyebrow text-accent-red">What you keep even if you cancel</div>
                <h3 className="mt-2 font-display text-2xl font-black uppercase sm:text-3xl">Zero data lost. Ever.</h3>
                <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                    Cancel today, rejoin in 6 months, and you land back at the same rank on the same PR sheet you left. Nothing archived, nothing deleted, no re-onboarding.
                </p>
                <div className="mt-6 grid gap-4 md:grid-cols-3">
                    {KEEP_IF_CANCEL.map((k, i) => (
                        <div key={i} className="border border-border bg-card/40 p-5" data-testid={`keep-item-${i}`}>
                            <div className="flex items-center gap-2 text-accent-red">
                                <k.icon className="h-4 w-4" />
                                <span className="label-eyebrow">{k.title}</span>
                            </div>
                            <p className="mt-2 text-sm text-white/80">{k.body}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Every-plan-includes — moved BELOW keep-if-cancel so the
                emotional frame lands first. Cognitive: feature dumps convert
                worse when placed above value framing. */}
            <div className="tactical-card p-8">
                <div className="label-eyebrow text-accent-red">Every plan includes</div>
                <ul className="mt-4 grid gap-3 md:grid-cols-2">
                    {FEATURES.map((f, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm"><Check className="mt-0.5 h-4 w-4 flex-none text-accent-red" />{f}</li>
                    ))}
                </ul>
            </div>

            {/* FAQ — objection handlers only. Every question here is one that
                measurably drops conversion when left unanswered ("What if I
                want to cancel? What if I skip a month? What if I'm in USD?"). */}
            <div className="tactical-card p-8" data-testid="pricing-faq">
                <div className="label-eyebrow text-accent-red">Straight answers</div>
                <h3 className="mt-2 font-display text-2xl font-black uppercase sm:text-3xl">Before you tap Subscribe.</h3>
                <Accordion type="single" collapsible className="mt-6 border-t border-white/10">
                    {PRICING_FAQ.map((item, i) => (
                        <AccordionItem key={i} value={`p-faq-${i}`} className="border-b border-white/10" data-testid={`pricing-faq-${i}`}>
                            <AccordionTrigger className="py-4 text-left font-display text-sm font-bold uppercase tracking-wide text-white hover:no-underline sm:text-base">
                                {item.q}
                            </AccordionTrigger>
                            <AccordionContent className="pb-4 text-sm text-white/75">
                                {item.a}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>

            {/* Trust footer — the science is our real moat. Repeat it on the
                pricing page so hesitation-at-checkout hits credibility, not
                a blank void. */}
            <div className="tactical-card p-8" data-testid="pricing-trust">
                <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-accent-red" />
                    <span className="label-eyebrow text-accent-red">Built on peer-reviewed research</span>
                </div>
                <h3 className="mt-2 font-display text-2xl font-black uppercase sm:text-3xl">Not gym-bro folklore. Not TikTok trends.</h3>
                <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                    Every training and nutrition principle inside Hypergains traces to a specific paper. Here are four of the load-bearing beams:
                </p>
                <div className="mt-6 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                    {[
                        { claim: "Mechanical tension drives hypertrophy", cite: "Schoenfeld, B.J. (2010). J. Strength & Cond. Res. PMID: 20847704" },
                        { claim: "Heavy 4–8 reps grow muscle as well as light high-rep sets", cite: "Schoenfeld et al. (2017). J. Strength & Cond. Res. PMID: 28834797" },
                        { claim: "1.6 g/kg protein is the muscle-building plateau", cite: "Morton et al. (2018). Br. J. Sports Med. PMID: 28698222" },
                        { claim: "You can lose fat AND add muscle simultaneously", cite: "Longland et al. (2016). Am. J. Clinical Nutrition. PMID: 26817506" },
                    ].map((s, i) => (
                        <div key={i} className="border border-white/10 bg-white/[0.02] p-4">
                            <p className="text-sm font-bold text-white">{s.claim}</p>
                            <p className="mt-2 text-[10px] uppercase tracking-wider text-white/50">{s.cite}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
